export const ESTIMATE_MINUTES_OPTIONS = [
  5, 10, 15, 30, 45, 60, 120, 180, 240, 
];

/** Select sentinel for a user-entered minute value (not sent to the API). */
export const ESTIMATE_CUSTOM_VALUE = 'custom';

/** Units for the custom estimate value + unit inputs. */
export const ESTIMATE_CUSTOM_UNITS = [
  { value: 'minutes', label: 'Minutes', minutesPerUnit: 1 },
  { value: 'hours', label: 'Hours', minutesPerUnit: 60 },
  { value: 'days', label: 'Days', minutesPerUnit: 1440 },
  { value: 'weeks', label: 'Weeks', minutesPerUnit: 10080 },
  { value: 'months', label: 'Months', minutesPerUnit: 43200 },
];

/** Max estimate for daily reminders (8 hours). */
export const DAILY_ESTIMATE_MAX_MINUTES = 480;

export const isPresetEstimateMinutes = (minutes) => {
  const n = Number(minutes);
  return Number.isFinite(n) && ESTIMATE_MINUTES_OPTIONS.includes(n);
};

export const getEstimateUnitMinutes = (unit) => {
  const match = ESTIMATE_CUSTOM_UNITS.find((u) => u.value === unit);
  return match ? match.minutesPerUnit : 1;
};

/**
 * Convert a custom value + unit into total minutes.
 * @returns {number|null}
 */
export const customEstimateToMinutes = (value, unit) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * getEstimateUnitMinutes(unit));
};

/**
 * Pick a friendly value + unit for an existing minute total (e.g. when editing).
 * Prefers the largest whole unit that divides evenly.
 */
export const minutesToCustomEstimate = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) {
    return { value: '', unit: 'minutes' };
  }

  const unitsLargestFirst = [...ESTIMATE_CUSTOM_UNITS].reverse();
  for (const unit of unitsLargestFirst) {
    if (n % unit.minutesPerUnit === 0) {
      return { value: String(n / unit.minutesPerUnit), unit: unit.value };
    }
  }
  return { value: String(n), unit: 'minutes' };
};

export const formatEstimateLabel = (m) => {
  if (m === 60) return '1 hour';
  if (m === 120) return '2 hours';
  if (m === 180) return '3 hours';
  if (m === 240) return '4 hours';
  if (m === 480) return '8 hours';
  if (m === 1440) return '1 day';
  if (m === 2880) return '2 days';
  if (m === 4320) return '3 days';
  if (m === 10080) return '1 week';
  if (m === 20160) return '2 weeks';
  if (m === 43200) return '1 month';
  return `${m} min`;
};

/** Format a unit amount with up to 2 decimal places (trailing zeros trimmed). */
const formatEstimateDecimal = (amount) => {
  const rounded = Math.round(amount * 100) / 100;
  return String(parseFloat(rounded.toFixed(2)));
};

export const formatEstimateTime = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '';

  if (n < 60) {
    return `${n} min`;
  }

  // Hours: up to 2 decimal places (e.g. 75 min → 1.25 hours)
  if (n < 1440) {
    const hours = n / 60;
    if (hours === 1) return '1 hour';
    return `${formatEstimateDecimal(hours)} hours`;
  }

  // Days: up to 2 decimal places
  if (n < 10080) {
    const days = n / 1440;
    if (days === 1) return '1 day';
    return `${formatEstimateDecimal(days)} days`;
  }

  if (n < 43200) {
    const weeks = n / 10080;
    if (weeks === 1) return '1 week';
    return Number.isInteger(weeks) ? `${weeks} weeks` : `${formatEstimateDecimal(weeks)} weeks`;
  }

  const months = n / 43200;
  if (months === 1) return '1 month';
  return Number.isInteger(months) ? `${months} months` : `${formatEstimateDecimal(months)} months`;
};

/**
 * Options for select, including custom value from existing entity when editing.
 * @param {number|string|null|undefined} existingMinutes
 * @param {{ recurrenceType?: string }} [options] - when recurrenceType is 'daily', caps at 8 hours
 */
export const estimateMinutesForSelect = (existingMinutes, { recurrenceType } = {}) => {
  const maxMinutes =
    recurrenceType === 'daily' ? DAILY_ESTIMATE_MAX_MINUTES : Number.POSITIVE_INFINITY;
  let list = ESTIMATE_MINUTES_OPTIONS.filter((m) => m <= maxMinutes);
  const n = existingMinutes != null && existingMinutes !== '' ? Number(existingMinutes) : NaN;
  if (!Number.isNaN(n) && n > 0 && n <= maxMinutes && !list.includes(n)) {
    list.push(n);
    list.sort((a, b) => a - b);
  }
  return list;
};
